/**
 * Dashboard Hooks - T138
 *
 * TanStack Query hooks for dashboard data
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useActiveTenantId } from './useTenantContext';
import type { TenantKey } from '../services/api/keys';

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

export interface DashboardMetrics {
  revenueThisMonth: number;
  revenueThisYear: number;
  revenueLastMonth: number;
  totalInvoices: number;
  invoicesDraft: number;
  invoicesSent: number;
  invoicesPaid: number;
  invoicesOverdue: number;
  outstandingBalance: number;
  overdueBalance: number;
  /** D3: Anzahl überfälliger Rechnungen (literal overdue + sent∧past-due) */
  overdueCount: number;
  totalCustomers: number;
  activeCustomers: number;
}

export interface RevenueByPeriod {
  period: string;
  revenue: number;
  invoiceCount: number;
  prevYearRevenue?: number;
  prevYearInvoiceCount?: number;
}

export interface OutstandingInvoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  grossTotal: number;
  dueDate: string;
  daysOverdue: number;
}

export interface OutstandingInvoicesResult {
  items: OutstandingInvoice[];
  /** Buch-Summe ALLER offenen Posten (Server-seitig, D3) */
  totalOutstanding: number;
  totalCount: number;
}

export interface RecentActivity {
  id: string;
  type: 'invoice_created' | 'invoice_paid' | 'invoice_sent' | 'customer_created';
  entityId: string;
  entityName: string;
  amount?: number;
  timestamp: string;
}

// ─────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────

export const dashboardKeys = {
  all: (t: TenantKey) => ['dashboard', t] as const,
  metrics: (t: TenantKey) => [...dashboardKeys.all(t), 'metrics'] as const,
  revenue: (t: TenantKey, months: number) => [...dashboardKeys.all(t), 'revenue', months] as const,
  outstanding: (t: TenantKey, limit: number) =>
    [...dashboardKeys.all(t), 'outstanding', limit] as const,
  activity: (t: TenantKey, limit: number) => [...dashboardKeys.all(t), 'activity', limit] as const,
};

// ─────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * Hook to get dashboard metrics
 */
export function useDashboardMetrics() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: dashboardKeys.metrics(tenantId),
    queryFn: () => api.get<DashboardMetrics>('/dashboard/metrics'),
    enabled: tenantId !== null,
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000, // Consider fresh for 30s
  });
}

/**
 * Hook to get revenue by period
 */
export function useRevenueByPeriod(months: number = 12) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: dashboardKeys.revenue(tenantId, months),
    queryFn: () => api.get<RevenueByPeriod[]>(`/dashboard/revenue?months=${months}`),
    enabled: tenantId !== null,
    staleTime: 60000,
  });
}

/**
 * Hook to get outstanding invoices
 */
export function useOutstandingInvoices(limit: number = 10) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: dashboardKeys.outstanding(tenantId, limit),
    queryFn: () =>
      api.get<OutstandingInvoicesResult>(`/dashboard/outstanding?limit=${limit}`),
    enabled: tenantId !== null,
    staleTime: 30000,
  });
}

/**
 * Hook to get recent activity
 */
export function useRecentActivity(limit: number = 10) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: dashboardKeys.activity(tenantId, limit),
    queryFn: () => api.get<RecentActivity[]>(`/dashboard/activity?limit=${limit}`),
    enabled: tenantId !== null,
    staleTime: 30000,
  });
}

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

export const activityTypeLabels: Record<RecentActivity['type'], string> = {
  invoice_created: 'Rechnung erstellt',
  invoice_sent: 'Rechnung versendet',
  invoice_paid: 'Zahlung erhalten',
  customer_created: 'Kunde angelegt',
};

export const activityTypeIcons: Record<RecentActivity['type'], string> = {
  invoice_created: '📝',
  invoice_sent: '📤',
  invoice_paid: '💰',
  customer_created: '👤',
};
